import { __assign, __read, __spread } from "tslib";
import React, { forwardRef, Fragment, useRef, useContext } from "react";
import classNames from "classnames";
import { Text } from "../text";
import { TableBox } from "./TableBox";
import { getRowKeyFromRecordKey } from "./util/get-row-key-from-record-key";
import { useMiddleware } from "./util/use-middleware";
import { SlideTransition } from "../transition";
import { useConfig } from "../_util/config-context";
import { TableVirtualizedBody, } from "./TableVirtualizedBody";
import { useResizeObserver } from "../_util/use-resize-observer";
import { DomRef } from "../domref";
import { noop } from "../_util/noop";
import { TableContext } from "./TableContext";
import { TableDraggableContext } from "./TableDraggableContext";
import { useIsomorphicLayoutEffect } from "../_util/use-isomorphic-layout-effect";
import { DragContext } from "./addons/draggable/DraggableTable";
import { getValueByPathKey } from "./util/get-value-by-path-key";
export function CellResizeObserver(_a) {
    var children = _a.children, onResize = _a.onResize;
    var ref = useRef(null);
    var width = useResizeObserver(ref).width;
    // 拖拽中不重置宽度
    var isDragging = (useContext(TableDraggableContext) || {}).isDragging;
    useIsomorphicLayoutEffect(function () {
        var didCancel = false;
        if (!didCancel && ref.current && width && !isDragging) {
            onResize(width);
        }
        return function () {
            didCancel = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width]);
    return React.createElement(DomRef, { ref: ref }, children);
}
// 计算固定列偏移
export function getFixedOffset(columns, index, columnsWidths, fixed) {
    var offset = 0;
    if (fixed === "left") {
        for (var i = 0; i < index; ++i) {
            if (columns[i].fixed === "left" && columnsWidths[i]) {
                offset += columnsWidths[i];
            }
        }
    }
    else {
        for (var i = columns.length - 1; i > index; --i) {
            if (columns[i].fixed === "right" && columnsWidths[i]) {
                offset += columnsWidths[i];
            }
        }
    }
    return offset;
}
// 获取固定列最后一列以显示阴影
export function getFixedEndColumn(columns) {
    var left = 0;
    var right = columns.length - 1;
    for (var i = 0; i < right; ++i) {
        if (columns[i].fixed === "left") {
            left = i;
        }
    }
    for (var i = right; i >= 0; --i) {
        if (columns[i].fixed === "right") {
            right = i;
        }
    }
    return { left: left, right: right };
}
// 表格组件核心实现
export var TableBody = forwardRef(function TableBody(_a, ref) {
    var columns = _a.columns, records = _a.records, addons = _a.addons, recordKey = _a.recordKey, rowClassName = _a.rowClassName, rowDisabled = _a.rowDisabled, topTip = _a.topTip, bottomTip = _a.bottomTip, tableBoxStyle = _a.tableBoxStyle, disableHoverHighlight = _a.disableHoverHighlight, disableTextOverflow = _a.disableTextOverflow, _b = _a.onScrollCapture, onScrollCapture = _b === void 0 ? noop : _b, draggable = _a.draggable, columnsDraggable = _a.columnsDraggable, virtualizedOptions = _a.virtualizedOptions, onResize = _a.onResize, recordsExpandable = _a.recordsExpandable;
    var classPrefix = useConfig().classPrefix;
    var _c = useContext(TableContext), scrollStatus = _c.scrollStatus, columnsWidths = _c.columnsWidths;
    var hasFixedColumn = columns.find(function (column) { return !!column.fixed; });
    var columnClassNames = useContext(DragContext).columnClassNames;
    // 键值的获取方式
    var getRowKey = getRowKeyFromRecordKey(recordKey);
    // 列渲染：(record, column) => { props, children }
    var renderColumn = useMiddleware(addons, "onInjectColumn")(function (record, rowKey, recordIndex, column, columnIndex) {
        var children = null;
        if (isFunction(column.render)) {
            children = column.render(record, rowKey, recordIndex, column, columnIndex);
        }
        else if (typeof record === "object" && record) {
            children = getValueByPathKey(record, column.key);
        }
        if (typeof children === "undefined") {
            // never render undefined
            children = null;
        }
        if (typeof children !== "object") {
            children = (React.createElement(Text, { overflow: !disableTextOverflow, title: disableTextOverflow ? undefined : String(children) }, children));
        }
        return {
            props: {},
            children: children,
        };
    });
    // 行渲染：(columns, record) => { prepends, row, appends }
    var renderRow = useMiddleware(addons, "onInjectRow")(function (record, rowKey, recordIndex, columns) {
        var className = classNames(isFunction(rowClassName) ? rowClassName(record) : null, isFunction(rowDisabled) ? { "is-disabled": rowDisabled(record) } : null, { "no-hover": !!disableHoverHighlight });
        return {
            prepends: [],
            row: (React.createElement("tr", { key: rowKey, className: className || null }, columns.map(function (column, index) {
                var _a, _b;
                var cKey = column.key;
                var _c = renderColumn(record, rowKey, recordIndex, column, index), props = _c.props, children = _c.children;
                if (props.colSpan === 0 || props.rowSpan === 0) {
                    return null;
                }
                // 开启虚拟滚动
                if (virtualizedOptions) {
                    props.style = __assign(__assign({}, (props.style || {})), { display: "inline-block", overflow: "hidden", boxSizing: "border-box", flex: column.width ? undefined : 1, flexBasis: column.width || 1 });
                }
                // 固定列
                if ((column.fixed === "left" || column.fixed === "right") &&
                    column.fixed !== scrollStatus &&
                    scrollStatus !== "no-scroll") {
                    var end = getFixedEndColumn(columns);
                    props.className = classNames(props.className, classPrefix + "-table__td--fixed", (_a = {},
                        _a[classPrefix + "-table__td--fixed-" + column.fixed] = end[column.fixed] === index,
                        _a));
                    props.style = __assign(__assign({}, (props.style || {})), (_b = { position: "sticky" }, _b[column.fixed] = getFixedOffset(columns, index, columnsWidths, column.fixed), _b));
                }
                // 拖拽排序
                if (columnsDraggable) {
                    props["data-column"] = cKey;
                    if (columnClassNames[cKey] &&
                        Array.isArray(columnClassNames[cKey])) {
                        props.className = classNames.apply(void 0, __spread([props.className], columnClassNames[cKey]));
                    }
                }
                var cell = (React.createElement(Text, __assign({ key: cKey, parent: "td" }, props, { align: column.align }),
                    React.createElement("div", null, children)));
                // 需要获取列宽
                if (recordIndex === 0 && (hasFixedColumn || draggable)) {
                    return (React.createElement(CellResizeObserver, { key: "" + cKey + index, onResize: function (width) {
                            onResize(function (widths) {
                                var columnsWidths = __spread(widths);
                                columnsWidths[index] = width;
                                return columnsWidths;
                            });
                        } }, cell));
                }
                return cell;
            }))),
            appends: [],
        };
    });
    // 记录渲染：(record) => <tr>[]
    var renderRecord = function (record, recordIndex) {
        // 生成 rowKey
        var rowKey = getRowKey(record, recordIndex);
        var _a = renderRow(record, rowKey, recordIndex, columns), prepends = _a.prepends, row = _a.row, appends = _a.appends;
        return __spread(prepends, [row], appends).filter(Boolean);
    };
    var renderBody = useMiddleware(addons, "onInjectBody")(function (records, columns, topTip, bottomTip) {
        // 开启虚拟滚动
        return virtualizedOptions ? (React.createElement(TableVirtualizedBody, __assign({ ref: ref, tableBoxStyle: tableBoxStyle, onScrollCapture: onScrollCapture, records: records, renderRecord: renderRecord, getRowKey: getRowKey, expandable: recordsExpandable, topTip: topTip && (React.createElement("tr", { className: classPrefix + "-table__tr--blank-new" },
                React.createElement("td", { colSpan: columns.length, style: {
                        overflow: "hidden",
                        flex: 1,
                        display: "inline-block",
                        width: "100%",
                    } }, topTip))), bottomTip: bottomTip && (React.createElement("tr", { className: classPrefix + "-table__tr--blank-new" },
                React.createElement("td", { colSpan: columns.length, style: {
                        overflow: "hidden",
                        flex: 1,
                        display: "inline-block",
                        width: "100%",
                    } }, bottomTip))) }, virtualizedOptions))) : (React.createElement("div", { className: classPrefix + "-table__body", ref: ref, onScrollCapture: onScrollCapture },
            React.createElement(TableBox, { columns: columns, style: tableBoxStyle, classPrefix: classPrefix },
                React.createElement("tbody", null,
                    React.createElement(SlideTransition, { in: Boolean(topTip), exit: false },
                        React.createElement("tr", { className: classPrefix + "-table__tr--blank-new" },
                            React.createElement("td", { colSpan: columns.length }, topTip))),
                    (records || []).map(function (record, index) { return (React.createElement(Fragment, { key: getRowKey(record, index) }, renderRecord(record, index))); }),
                    React.createElement(SlideTransition, { in: Boolean(bottomTip), exit: false },
                        React.createElement("tr", { className: classPrefix + "-table__tr--blank-new" },
                            React.createElement("td", { colSpan: columns.length }, bottomTip)))))));
    });
    return renderBody(records, columns, topTip, bottomTip);
});
TableBody.displayName = "TableBody";
function isFunction(target) {
    return typeof target === "function";
}
//# sourceMappingURL=TableBody.js.map